import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../api/apiService';
import type { Exercise, Category, User, TrainingCreate } from '../../types';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { ArrowLeft, Trash2, ChevronUp, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { Modal } from '../../components/ui/Modal';
import './CreateTraining.css';

interface TrainingFormExercise {
  exercise: Exercise;
}

type SectionType = 'warmup' | 'main' | 'cooldown';

export const CreateTraining: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();

  // Available data
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Form state
  const [title, setTitle] = useState('');
  const [userId, setUserId] = useState(0);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [description, setDescription] = useState('');
  const [restBetweenExercises, setRestBetweenExercises] = useState(15);

  // Global parameters for warmup section
  const [warmupSeconds, setWarmupSeconds] = useState(30);
  const [warmupRest, setWarmupRest] = useState(10);

  // Global parameters for main section
  const [mainSeries, setMainSeries] = useState(2);
  const [mainSeconds, setMainSeconds] = useState(45);
  const [mainRestBetweenSeries, setMainRestBetweenSeries] = useState(90);

  // Global parameters for cooldown section
  const [cooldownSeconds, setCooldownSeconds] = useState(30);
  const [cooldownRest, setCooldownRest] = useState(10);

  // Exercise sections
  const [warmup, setWarmup] = useState<TrainingFormExercise[]>([]);
  const [main, setMain] = useState<TrainingFormExercise[]>([]);
  const [cooldown, setCooldown] = useState<TrainingFormExercise[]>([]);

  const [activeSection, setActiveSection] = useState<SectionType>('warmup');

  // Collapsed categories state
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Exercise info modal state
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Reset expanded categories when changing section
  useEffect(() => {
    setExpandedCategories(new Set());
  }, [activeSection]);

  const loadData = async () => {
    try {
      const [exercisesData, categoriesData, usersData] = await Promise.all([
        apiService.getExercises(),
        apiService.getCategories(),
        apiService.getCoachTrainees(),
      ]);
      setExercises(exercisesData);
      setCategories(categoriesData);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(t('error'));
    } finally {
      setIsLoading(false);
    }
  };

  const getCategoryName = (exercise: Exercise): string => {
    if (exercise.category?.name) {
      return exercise.category.name;
    }
    const categoryId = exercise.category_id || exercise.category?.id || 0;
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || 'Sin categoría';
  };

  // Función para verificar si un ejercicio es válido para la sección activa
  const isExerciseValidForSection = (exercise: Exercise, section: SectionType): boolean => {
    const categoryId = exercise.category_id || exercise.category?.id;
    if (!categoryId) return true; // Si no tiene categoría, mostrarlo

    const category = categories.find((c) => c.id === categoryId);
    if (!category) return true;

    const phases = category.phases || 'all';

    // Si la categoría es para todas las fases, siempre es válida
    if (phases === 'all') return true;

    // Mapeo de secciones a nombres de fase
    const sectionPhaseMap: Record<SectionType, string> = {
      'warmup': 'warmup',
      'main': 'main',
      'cooldown': 'cooldown'
    };

    const requiredPhase = sectionPhaseMap[section];
    return phases.includes(requiredPhase);
  };

  const groupExercisesByCategory = (exercisesList: Exercise[], section: SectionType) => {
    // Filtrar ejercicios válidos para la sección
    const validExercises = exercisesList.filter(ex => isExerciseValidForSection(ex, section));

    const grouped: Record<string, Exercise[]> = {};
    validExercises.forEach((exercise) => {
      const categoryName = getCategoryName(exercise);
      if (!grouped[categoryName]) {
        grouped[categoryName] = [];
      }
      grouped[categoryName].push(exercise);
    });

    // Ordenar ejercicios dentro de cada categoría alfabéticamente
    Object.keys(grouped).forEach(categoryName => {
      grouped[categoryName].sort((a, b) => a.name.localeCompare(b.name));
    });

    return grouped;
  };

  const isExerciseInSection = (exerciseId: number, section: TrainingFormExercise[]): boolean => {
    return section.some((item) => item.exercise.id === exerciseId);
  };

  const canAddExercise = (exercise: Exercise): boolean => {
    const currentSection = activeSection === 'warmup' ? warmup : activeSection === 'main' ? main : cooldown;
    return !isExerciseInSection(exercise.id, currentSection);
  };

  const handleAddExercise = (exercise: Exercise) => {
    if (!canAddExercise(exercise)) {
      return;
    }

    const newExercise: TrainingFormExercise = {
      exercise,
    };

    if (activeSection === 'warmup') {
      setWarmup([...warmup, newExercise]);
    } else if (activeSection === 'main') {
      setMain([...main, newExercise]);
    } else {
      setCooldown([...cooldown, newExercise]);
    }
  };

  const handleRemoveExercise = (section: SectionType, index: number) => {
    if (section === 'warmup') {
      setWarmup(warmup.filter((_, i) => i !== index));
    } else if (section === 'main') {
      setMain(main.filter((_, i) => i !== index));
    } else {
      setCooldown(cooldown.filter((_, i) => i !== index));
    }
  };

  const handleMoveExercise = (section: SectionType, index: number, direction: 'up' | 'down') => {
    const moveInArray = (arr: TrainingFormExercise[]) => {
      const newArr = [...arr];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= newArr.length) return arr;
      [newArr[index], newArr[targetIndex]] = [newArr[targetIndex], newArr[index]];
      return newArr;
    };

    if (section === 'warmup') {
      setWarmup(moveInArray(warmup));
    } else if (section === 'main') {
      setMain(moveInArray(main));
    } else {
      setCooldown(moveInArray(cooldown));
    }
  };

  const handleShowExerciseInfo = (exercise: Exercise) => {
    setSelectedExercise(exercise);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedExercise(null);
  };

  const handleSubmit = async () => {
    // Validation
    if (!title.trim()) {
      toast.error('El título es requerido');
      return;
    }
    if (!userId) {
      toast.error('Debe seleccionar un usuario');
      return;
    }
    if (!date) {
      toast.error('La fecha es requerida');
      return;
    }

    if (warmup.length === 0 && main.length === 0 && cooldown.length === 0) {
      toast.error('Debe agregar al menos un ejercicio');
      return;
    }

    try {
      const trainingData: TrainingCreate = {
        title,
        date: time ? `${date}T${time}` : date,
        user_id: userId,
        observations: description,
        rest_between_exercises: restBetweenExercises,
      };

      // Add warmup section if there are exercises
      if (warmup.length > 0) {
        trainingData.warmup_section = {
          exercises: warmup.map((item, index) => ({
            exercise_id: item.exercise.id,
            position: index,
          })),
          params: {
            series: 1,
            repetitions: 0,
            seconds: warmupSeconds,
            rest_between_series: 0,
            rest_between_exercises: warmupRest,
          },
        };
      }

      // Add main section if there are exercises
      if (main.length > 0) {
        trainingData.main_section = {
          exercises: main.map((item, index) => ({
            exercise_id: item.exercise.id,
            position: index,
          })),
          params: {
            series: mainSeries,
            repetitions: 0,
            seconds: mainSeconds,
            rest_between_series: mainRestBetweenSeries,
            rest_between_exercises: restBetweenExercises,
          },
        };
      }

      // Add cooldown section if there are exercises
      if (cooldown.length > 0) {
        trainingData.cooldown_section = {
          exercises: cooldown.map((item, index) => ({
            exercise_id: item.exercise.id,
            position: index,
          })),
          params: {
            series: 1,
            repetitions: 0,
            seconds: cooldownSeconds,
            rest_between_series: 0,
            rest_between_exercises: cooldownRest,
          },
        };
      }

      await apiService.createTraining(trainingData);
      toast.success(t('trainingCreated'));
      navigate('/coach/trainings');
    } catch (error) {
      console.error('Error creating training:', error);
      toast.error(t('error'));
    }
  };

  const groupedAvailableExercises = groupExercisesByCategory(exercises, activeSection);

  const toggleCategory = (categoryName: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName);
    } else {
      newExpanded.add(categoryName);
    }
    setExpandedCategories(newExpanded);
  };

  const isCategoryExpanded = (categoryName: string) => {
    return expandedCategories.has(categoryName);
  };

  // Calculate estimated time for warmup section
  const calculateWarmupTime = (): number => {
    if (warmup.length === 0) return 0;
    let totalSeconds = warmup.length * warmupSeconds; // Time per exercise
    totalSeconds += (warmup.length - 1) * warmupRest; // Rest between exercises
    return totalSeconds;
  };

  // Calculate estimated time for main section
  const calculateMainTime = (): number => {
    if (main.length === 0) return 0;
    let totalSeconds = main.length * mainSeries * mainSeconds; // Time per exercise * series
    totalSeconds += main.length * (mainSeries - 1) * mainRestBetweenSeries; // Rest between series
    totalSeconds += (main.length - 1) * restBetweenExercises; // Rest between exercises
    return totalSeconds;
  };

  // Calculate estimated time for cooldown section
  const calculateCooldownTime = (): number => {
    if (cooldown.length === 0) return 0;
    let totalSeconds = cooldown.length * cooldownSeconds; // Time per exercise
    totalSeconds += (cooldown.length - 1) * cooldownRest; // Rest between exercises
    return totalSeconds;
  };

  // Calculate estimated training time in seconds
  const calculateEstimatedTime = (): number => {
    return calculateWarmupTime() + calculateMainTime() + calculateCooldownTime();
  };

  // Format time in minutes and seconds
  const formatTime = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes > 0 && seconds > 0) {
      return `${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  };

  if (isLoading) {
    return <div className="text-center p-4">{t('loading')}</div>;
  }

  return (
    <div className="create-training-page">
      <div className="page-header">
        <Button variant="secondary" onClick={() => navigate('/coach/trainings')}>
          <ArrowLeft size={20} />
          {t('goBack')}
        </Button>
        <h1 className="page-title">{t('createTraining')}</h1>
      </div>

      <div className="create-training-layout">
        {/* Left: Exercise Selection */}
        <div className="exercise-selection-panel">
          <h2 className="panel-title">{t('availableExercises')}</h2>
          <p className="panel-subtitle">{t('clickToAdd')}</p>
          <div className="exercises-by-category">
            {Object.entries(groupedAvailableExercises)
              .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
              .map(([categoryName, categoryExercises]) => {
              const isExpanded = isCategoryExpanded(categoryName);
              return (
                <div key={categoryName} className="category-group">
                  <div
                    className="category-group-header"
                    onClick={() => toggleCategory(categoryName)}
                  >
                    <div className="category-group-header-content">
                      {isExpanded ? (
                        <ChevronDown size={20} className="category-chevron" />
                      ) : (
                        <ChevronRight size={20} className="category-chevron" />
                      )}
                      <h3 className="category-group-title">{categoryName}</h3>
                    </div>
                    <span className="category-exercise-count">
                      {categoryExercises.length} {categoryExercises.length === 1 ? t('exercise').toLowerCase() : t('exercises').toLowerCase()}
                    </span>
                  </div>
                  {isExpanded && (
                    <div className="exercise-chips">
                      {categoryExercises.map((exercise) => (
                        <button
                          key={exercise.id}
                          className={`exercise-chip ${!canAddExercise(exercise) ? 'disabled' : ''}`}
                          onClick={() => handleAddExercise(exercise)}
                          disabled={!canAddExercise(exercise)}
                        >
                          {exercise.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Center: Training Sections */}
        <div className="training-sections">
          {/* Warm-up Section */}
          <Card className={`section-card ${activeSection === 'warmup' ? 'active' : ''}`}>
            <div className="section-header" onClick={() => setActiveSection('warmup')}>
              <div className="section-header-left">
                <h3 className="section-title">{t('warmup')}</h3>
                <span className="exercise-count">{warmup.length} {t('exercises').toLowerCase()}</span>
              </div>
              <div className="section-header-params" onClick={(e) => e.stopPropagation()}>
                <label className="config-label-inline">
                  {t('seconds')}:
                  <input
                    type="number"
                    value={warmupSeconds}
                    onChange={(e) => setWarmupSeconds(Number(e.target.value))}
                    className="config-input-inline"
                  />
                </label>
                <label className="config-label-inline">
                  Descanso:
                  <input
                    type="number"
                    value={warmupRest}
                    onChange={(e) => setWarmupRest(Number(e.target.value))}
                    className="config-input-inline"
                  />
                </label>
              </div>
              <div className="section-chevron">
                {activeSection === 'warmup' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </div>
            {activeSection === 'warmup' && (
              <div className="section-content">
                {warmup.length === 0 ? (
                  <p className="empty-section">{t('noExercisesAdded')}</p>
                ) : (
                  <div className="exercise-list">
                    {warmup.map((item, index) => (
                      <div key={`warmup-${item.exercise.id}`} className="exercise-item">
                        <div className="exercise-info">
                          <span className="exercise-number">{index + 1}</span>
                          <span className="exercise-name">{item.exercise.name}</span>
                          <span className="exercise-category">{getCategoryName(item.exercise)}</span>
                        </div>
                        <div className="exercise-actions">
                          <button
                            className="icon-button info"
                            onClick={() => handleShowExerciseInfo(item.exercise)}
                            title="Ver información"
                          >
                            <Info size={16} />
                          </button>
                          <button
                            className="icon-button"
                            onClick={() => handleMoveExercise('warmup', index, 'up')}
                            disabled={index === 0}
                          >
                            <ChevronUp size={16} />
                          </button>
                          <button
                            className="icon-button"
                            onClick={() => handleMoveExercise('warmup', index, 'down')}
                            disabled={index === warmup.length - 1}
                          >
                            <ChevronDown size={16} />
                          </button>
                          <button
                            className="icon-button danger"
                            onClick={() => handleRemoveExercise('warmup', index)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Main Section */}
          <Card className={`section-card ${activeSection === 'main' ? 'active' : ''}`}>
            <div className="section-header" onClick={() => setActiveSection('main')}>
              <div className="section-header-left">
                <h3 className="section-title">{t('mainPart')}</h3>
                <span className="exercise-count">{main.length} {t('exercises').toLowerCase()}</span>
              </div>
              <div className="section-header-params main-params-grid" onClick={(e) => e.stopPropagation()}>
                <label className="config-label-inline">
                  {t('series')}:
                  <input
                    type="number"
                    value={mainSeries}
                    onChange={(e) => setMainSeries(Number(e.target.value))}
                    className="config-input-inline"
                  />
                </label>
                <label className="config-label-inline">
                  {t('seconds')}:
                  <input
                    type="number"
                    value={mainSeconds}
                    onChange={(e) => setMainSeconds(Number(e.target.value))}
                    className="config-input-inline"
                  />
                </label>
                <label className="config-label-inline">
                  {t('restBetweenSeries')}:
                  <input
                    type="number"
                    value={mainRestBetweenSeries}
                    onChange={(e) => setMainRestBetweenSeries(Number(e.target.value))}
                    className="config-input-inline"
                  />
                </label>
                <label className="config-label-inline">
                  {t('restBetweenExercises')}:
                  <input
                    type="number"
                    value={restBetweenExercises}
                    onChange={(e) => setRestBetweenExercises(Number(e.target.value))}
                    className="config-input-inline"
                  />
                </label>
              </div>
              <div className="section-chevron">
                {activeSection === 'main' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </div>
            {activeSection === 'main' && (
              <div className="section-content">
                {main.length === 0 ? (
                  <p className="empty-section">{t('noExercisesAdded')}</p>
                ) : (
                  <div className="exercise-list">
                    {main.map((item, index) => (
                      <div key={`main-${item.exercise.id}`} className="exercise-item">
                        <div className="exercise-info">
                          <span className="exercise-number">{index + 1}</span>
                          <span className="exercise-name">{item.exercise.name}</span>
                          <span className="exercise-category">{getCategoryName(item.exercise)}</span>
                        </div>
                        <div className="exercise-actions">
                          <button
                            className="icon-button info"
                            onClick={() => handleShowExerciseInfo(item.exercise)}
                            title="Ver información"
                          >
                            <Info size={16} />
                          </button>
                          <button
                            className="icon-button"
                            onClick={() => handleMoveExercise('main', index, 'up')}
                            disabled={index === 0}
                          >
                            <ChevronUp size={16} />
                          </button>
                          <button
                            className="icon-button"
                            onClick={() => handleMoveExercise('main', index, 'down')}
                            disabled={index === main.length - 1}
                          >
                            <ChevronDown size={16} />
                          </button>
                          <button
                            className="icon-button danger"
                            onClick={() => handleRemoveExercise('main', index)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Cool-down Section */}
          <Card className={`section-card ${activeSection === 'cooldown' ? 'active' : ''}`}>
            <div className="section-header" onClick={() => setActiveSection('cooldown')}>
              <div className="section-header-left">
                <h3 className="section-title">{t('cooldown')}</h3>
                <span className="exercise-count">{cooldown.length} {t('exercises').toLowerCase()}</span>
              </div>
              <div className="section-header-params" onClick={(e) => e.stopPropagation()}>
                <label className="config-label-inline">
                  {t('seconds')}:
                  <input
                    type="number"
                    value={cooldownSeconds}
                    onChange={(e) => setCooldownSeconds(Number(e.target.value))}
                    className="config-input-inline"
                  />
                </label>
                <label className="config-label-inline">
                  Descanso:
                  <input
                    type="number"
                    value={cooldownRest}
                    onChange={(e) => setCooldownRest(Number(e.target.value))}
                    className="config-input-inline"
                  />
                </label>
              </div>
              <div className="section-chevron">
                {activeSection === 'cooldown' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </div>
            {activeSection === 'cooldown' && (
              <div className="section-content">
                {cooldown.length === 0 ? (
                  <p className="empty-section">{t('noExercisesAdded')}</p>
                ) : (
                  <div className="exercise-list">
                    {cooldown.map((item, index) => (
                      <div key={`cooldown-${item.exercise.id}`} className="exercise-item">
                        <div className="exercise-info">
                          <span className="exercise-number">{index + 1}</span>
                          <span className="exercise-name">{item.exercise.name}</span>
                          <span className="exercise-category">{getCategoryName(item.exercise)}</span>
                        </div>
                        <div className="exercise-actions">
                          <button
                            className="icon-button info"
                            onClick={() => handleShowExerciseInfo(item.exercise)}
                            title="Ver información"
                          >
                            <Info size={16} />
                          </button>
                          <button
                            className="icon-button"
                            onClick={() => handleMoveExercise('cooldown', index, 'up')}
                            disabled={index === 0}
                          >
                            <ChevronUp size={16} />
                          </button>
                          <button
                            className="icon-button"
                            onClick={() => handleMoveExercise('cooldown', index, 'down')}
                            disabled={index === cooldown.length - 1}
                          >
                            <ChevronDown size={16} />
                          </button>
                          <button
                            className="icon-button danger"
                            onClick={() => handleRemoveExercise('cooldown', index)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* Right: Training Summary */}
        <div className="training-summary">
          <Card>
            <h2 className="panel-title">{t('trainingSummary')}</h2>
            <div className="summary-form">
              <Input
                label={t('title')}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('trainingTitle')}
              />
              <div className="input-wrapper">
                <label className="input-label">{t('assignedUser')}</label>
                <select
                  className="input"
                  value={userId}
                  onChange={(e) => setUserId(Number(e.target.value))}
                >
                  <option value={0} disabled>{t('selectUser')}</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label={t('date')}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
              <Input
                label={t('time')}
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
              <div className="input-wrapper">
                <label className="input-label">{t('description')}</label>
                <textarea
                  className="input textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('trainingDescription')}
                  rows={3}
                />
              </div>
            </div>
            <div className="summary-stats">
              <div className="stat-item">
                <span className="stat-label">{t('warmup')}:</span>
                <span className="stat-value">
                  {warmup.length} {warmup.length > 0 && (
                    <span className="stat-time">({formatTime(calculateWarmupTime())})</span>
                  )}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">{t('mainPart')}:</span>
                <span className="stat-value">
                  {main.length} {main.length > 0 && (
                    <span className="stat-time">({formatTime(calculateMainTime())})</span>
                  )}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">{t('cooldown')}:</span>
                <span className="stat-value">
                  {cooldown.length} {cooldown.length > 0 && (
                    <span className="stat-time">({formatTime(calculateCooldownTime())})</span>
                  )}
                </span>
              </div>
              <div className="stat-item stat-total">
                <span className="stat-label">{t('totalExercises')}:</span>
                <span className="stat-value">{warmup.length + main.length + cooldown.length}</span>
              </div>
              <div className="stat-item stat-total">
                <span className="stat-label">Tiempo estimado:</span>
                <span className="stat-value">{formatTime(calculateEstimatedTime())}</span>
              </div>
            </div>
            <Button variant="primary" onClick={handleSubmit} className="create-button">
              {t('createTraining')}
            </Button>
          </Card>
        </div>
      </div>

      {/* Exercise Info Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedExercise?.name || ''}
      >
        {selectedExercise && (
          <div className="exercise-modal-content">
            {selectedExercise.imageUrl && (
              <div className="exercise-modal-image">
                <img src={selectedExercise.imageUrl} alt={selectedExercise.name} />
              </div>
            )}

            {selectedExercise.description && (
              <div className="exercise-modal-section">
                <h3 className="exercise-modal-subtitle">{t('description')}</h3>
                <p className="exercise-modal-text">{selectedExercise.description}</p>
              </div>
            )}

            <div className="exercise-modal-section">
              <h3 className="exercise-modal-subtitle">{t('category')}</h3>
              <p className="exercise-modal-text">{getCategoryName(selectedExercise)}</p>
            </div>

            {selectedExercise.videoUrl && (
              <div className="exercise-modal-section">
                <h3 className="exercise-modal-subtitle">{t('video')}</h3>
                <a
                  href={selectedExercise.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="exercise-modal-link"
                >
                  {t('watchVideo')}
                </a>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};
